import { CaretDownOutlined, CaretUpOutlined, DoubleRightOutlined } from '@ant-design/icons';
import useTranslation from 'next-translate/useTranslation';
import {
  SuggestedPriceAmountKey,
  SuggestedPriceCalculationStep,
  SuggestedPricePercentKey,
} from '@features/suggested-price-column/components/suggested-price-stepper/suggested-price-calculations';
import { displayCurrencyWithPrecision } from '@utils/currency';

import styles from './suggested-price-stepper.module.scss';

const COPY_SCOPE = 'bid_request.line_items.suggested_price.explanation';

interface SuggestedPriceStepperProps {
  amounts: Partial<Record<SuggestedPriceAmountKey, number | null>>;
  currency: string;
  percents: Partial<Record<SuggestedPricePercentKey, number | null>>;
  pricePrecision: number;
  steps: SuggestedPriceCalculationStep[];
}

/**
 * The Negotiation Starting Price → AI → final progression at the top of the info drawer, rendered as tinted tiles
 * separated by chevrons.
 *
 * Tiles are driven entirely by the `steps` data (see `suggested-price-calculations.ts`), so the
 * caller picks a variant rather than composing markup: the same component renders the
 * AI-generated and overridden layouts.
 */
export const SuggestedPriceStepper = ({
  amounts,
  currency,
  percents,
  pricePrecision,
  steps,
}: SuggestedPriceStepperProps) => {
  const { t } = useTranslation('common');
  const unavailable = t(`${COPY_SCOPE}.unavailable`);

  const price = (key?: SuggestedPriceAmountKey) => {
    const value = key ? amounts[key] : null;

    return value == null ? unavailable : displayCurrencyWithPrecision(value, currency, pricePrecision);
  };

  const trend = (key?: SuggestedPricePercentKey) => {
    const value = key ? percents[key] : null;

    if (value == null) return null;

    return { isDiscount: value >= 0, label: `${Math.abs(value).toFixed(1)}%` };
  };

  return (
    <div className={styles.stepper} data-testid='suggested-price-stepper'>
      {steps.map((step, index) => {
        const percent = trend(step.percent);

        return (
          <div key={step.labelKey} className={styles.stepWrapper}>
            {index > 0 && <DoubleRightOutlined aria-hidden className={styles.separator} />}
            <div className={styles.step} data-testid={`suggested-price-step-${step.labelKey}`}>
              <span className={styles.label}>{t(`${COPY_SCOPE}.${step.labelKey}`)}</span>
              {step.render === 'percent' ? (
                <span className={percent?.isDiscount === false ? styles.trendPremium : styles.trend}>
                  {percent == null ? unavailable : (
                    <>
                      {percent.isDiscount ? <CaretDownOutlined aria-hidden /> : <CaretUpOutlined aria-hidden />}
                      {percent.label}
                    </>
                  )}
                </span>
              ) : (
                <span className={styles.value}>{price(step.amount)}</span>
              )}
              {step.render === 'stacked' && percent != null && (
                <span className={percent.isDiscount ? styles.trendStacked : styles.trendStackedPremium}>
                  ({percent.isDiscount ? <CaretDownOutlined aria-hidden /> : <CaretUpOutlined aria-hidden />}
                  {percent.label})
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
